<template>
    <div class="chat-voting" ref="voting"
         :class="{'start-voting': startVote,
         'ended-voting': isVotingEnded(message),
         'is-group': message.data.optionsGroups.length > 1}">

        <template v-if="loaderProgress && message.data.endTime && !message.data.ended">
            <div class="chat-voting__loader-wrapper">
                <span class="chat-voting__time-left" v-if="secondsLeft">{{timeLeft}}</span>
                <div class="chat-voting__loader">
                    <span class="chat-voting__loader-progress" ref="progress"
                      :style="{
                        'width': `${loaderProgress}%`}"></span>
                </div>
            </div>
        </template>
        <template v-if="!message.data.endTime && isVotingStarted(message)">
            <div class="chat-voting__loader-wrapper">
                <span class="chat-voting__time-left" v-if="secondsLeft">{{timeLeft}}</span>
                <div class="chat-voting__loader">
                    <span class="chat-voting__loader-progress"
                      :style="{
                        'width': '100%'}"></span>
                </div>
            </div>
        </template>

        <template v-if="isVotingStarted(message)">
            <template v-for="(optionsGroup, index) in message.data.optionsGroups">
                <span class="chat-voting__group-title" v-if="optionsGroup.title" v-html="optionsGroup.title"></span>

                <template v-if="optionsGroup.voteType === types.voteForPlayer">
                    <div class="chat-voting__players-voting chat-voting-group"
                         :class="{'last': index === message.data.optionsGroups - 1}">
                        <div class="chat-voting__players-voting-item"
                             v-for="(option, optionIndex) in optionsGroup.options"
                             @click="countVote(index, option.text)"
                             :key="`${index}_${optionIndex}_${option.disabledFor.length}`"
                             v-if="isVisibleOption(option)"
                        >
                            <button type="button"
                                    :disabled="isDisabledOption(option)"
                                    :class="{'disabled': isDisabledOption(option), 'selected-option': isSelectedOption(option)}"
                                    :style="{'background': getPlayerColor(option.text)}"
                            ></button>
                        </div>
                    </div>
                </template>
                <template v-if="optionsGroup.voteType === types.voteForVariant">
                    <div class="chat-voting__vote-for-variant chat-voting-group"
                         :class="[getVotingLayoutClass(index), hasSelectedOption(optionsGroup.options)]">
                        <div class="chat-voting__voting-item" v-for="(option, optionIndex) in optionsGroup.options"
                             :key="`${index}_${optionIndex}_${option.votesCount}`"
                        >
                            <button type="button" class="yellow-variant-button"
                                    ref="variantButton"
                                    :disabled="isDisabledOption(option)"
                                    @click="countVote(index, option.text)"
                                    :class="{'disabled': isDisabledOption(option), 'selected-option': isSelectedOption(option)}">
                                <span>{{ option.text }}</span>
                            </button>
                        </div>
                    </div>
                </template>
                <template v-if="optionsGroup.voteType === types.forAgainstVoting">
                    <div class="chat-voting__for-against-voting chat-voting-group"
                         :class="{'last': index === message.data.optionsGroups - 1}">
                        <div class="question">{{ optionsGroup.text }}</div>
                        <div class="like-btn-wrapper" v-for="(option, optionIndex) in optionsGroup.options" :key="optionIndex">
                            <button type="button"
                                    :class="{'dislike-btn': option.text === 'no', 'like-btn': option.text === 'yes'}"
                                    @click="countVote(index, option.text)"
                                    :disabled="isDisabledOption(option)">
                                <svg v-if="option.text === 'yes'">
                                    <use xlink:href="#ic_like"></use>
                                </svg>
                                <svg v-if="option.text === 'no'">
                                    <use xlink:href="#ic_like"></use>
                                </svg>
                            </button>
                        </div>
                    </div>
                </template>
            </template>
        </template>

        <template v-if="isVotingEnded(message)">
            <template v-for="(optionsGroup, index) in message.data.optionsGroups">
                <span v-if="optionsGroup.title" class="chat-voting__group-title" v-html="optionsGroup.title"></span>

                <template v-if="optionsGroup.voteType === types.voteForPlayer">
                    <div class="chat-voting__players-voting chat-voting-group"
                         :class="{'last': index === message.data.optionsGroups - 1}">
                        <div class="chat-voting__players-voting-item" v-for="option in optionsGroup.options"
                             :class="{'winner': isWinner(index, option)}">
                            <button type="button"
                                    :disabled="!getVotedPlayers(index, option.text).length"
                                    :style="{'background': getPlayerColor(option.text)}"
                            ></button>
                            <div class="voted-players" v-if="getVotedPlayers(index, option.text).length">
                                <span v-for="votedPlayer in getVotedPlayers(index, option.text)"
                                      :style="{'background': votedPlayer.color ? votedPlayer.color.code : 'linear-gradient(180deg, #FFB800 0%, #FF7A00 100%)'}"></span>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-if="optionsGroup.voteType === types.voteForVariant && message.data.optionsGroups.length">
                    <div class="chat-voting__vote-for-variant chat-voting-group"
                         :class="getVotingLayoutClass(index)">
                        <div class="chat-voting__voting-item" v-for="option in optionsGroup.options"
                             :class="{'winner': isWinner(index, option)}">
                            <button type="button"
                                    class="yellow-variant-button"
                                    ref="variantButton">
                                <span>{{ option.text }}</span>
                            </button>
                            <div class="voted-players" v-if="getVotedPlayers(index, option.text).length">
                                <span v-for="votedPlayer in getVotedPlayers(index, option.text)"
                                      :style="{'background': votedPlayer.color ? votedPlayer.color.code : 'linear-gradient(180deg, #FFB800 0%, #FF7A00 100%)'}"></span>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-if="optionsGroup.voteType === types.forAgainstVoting">
                    <div class="chat-voting__for-against-voting chat-voting-group"
                         :class="{'last': index === message.data.optionsGroups - 1}">
                        <div class="question">{{ optionsGroup.text }}</div>
                        <div class="like-btn-wrapper" v-for="option in optionsGroup.options">
                            <button type="button"
                                    :class="{'like-btn': option.text === 'yes',
                                    'dislike-btn': option.text === 'no'}"
                                    :disabled="!isWinner(index, option)">
                                <svg v-if="option.text === 'yes'">
                                    <use xlink:href="#ic_like"></use>
                                </svg>
                                <svg v-if="option.text === 'no'">
                                    <use xlink:href="#ic_like"></use>
                                </svg>
                            </button>
                            <div class="voted-players" v-if="getVotedPlayers(index, option.text).length">
                                <span v-for="votedPlayer in getVotedPlayers(index, option.text)"
                                      :style="{'background': votedPlayer.color ? votedPlayer.color.code : 'linear-gradient(180deg, #FFB800 0%, #FF7A00 100%)'}">
                                </span>
                            </div>
                        </div>

                    </div>
                </template>
            </template>
        </template>
    </div>
</template>

<script>
import EventApi from '../../../../api/EventsApi';
import store from '../../../../store';

const MESSAGE_TYPE_VOTE = 1;

export default {
    props: {
        message: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            types: {
                voteForPlayer: 1,
                voteForVariant: 2,
                forAgainstVoting: 3
            },
            loaderProgress: 100,
            timer: null,
            isVoted: false,
            buttonHeight: 24,
            startVote: false,
            endedVote: false,
            secondsLeft: 0
        }
    },

    mounted() {
        if (this.message.data.endTime) {
            this.$nextTick(() => {
                this.startTimer();
            });
        }
        if (this.isVotingStarted(this.message)) {
            this.setMessagesListPadding(this.$refs.voting.offsetHeight);
        } else {
            this.setMessagesListPadding(0);
        }

        if (this.isVotingStarted(this.message)) {
            setTimeout(() => {
                this.startVote = true;
            }, 100);
        }
    },

    beforeDestroy() {
        this.setMessagesListPadding(0);
        this.loaderProgress = 0;
        clearTimeout(this.timer);
        this.timer = null;
    },

    watch: {
        message: function (newMessage, oldMessage) {
            if (newMessage.data && newMessage.data.ended) {
                this.setMessagesListPadding(0);
                this.loaderProgress = 0;
                clearTimeout(this.timer);
                this.timer = null;
            }
        }
    },

    destroyed() {
        EventApi.triggerEvent(EventApi.REFRESH_SCROLL_CHAT);
    },

    computed: {
        players: () => store.getters.getPlayers,
        playerId: () => store.getters.getPlayerId,

        getPlayerStartedVotingColor: function () {
            return this.getPlayerColor(this.message.playerId);
        },

        getCurrentPlayerColor: function () {
            return this.getPlayerColor(this.playerId);
        },

        timeLeft: function() {
            const minutes = Math.floor(this.secondsLeft / 60);
            const seconds = this.secondsLeft - minutes * 60;
            const minutesStr = minutes > 9 ? minutes : `0${minutes}`;
            const secondsStr = seconds > 9 ? seconds : `0${seconds}`;

            return `${minutesStr}:${secondsStr}`;
        }
    },

    methods: {
        hasSelectedOption(options) {
            let result = '';

            options.forEach((option) => {
                if (this.isSelectedOption(option)) {
                    result = 'has-selected-option';
                }
            })

            return result;
        },

        getVotingLayoutClass(index)
        {
            if (!this.message.data.optionsGroups[index].options.length) {
                return null;
            }

            let cssClass = 'is-row';

            this.message.data.optionsGroups[index].options.forEach((item) => {
                if (item.text.length >= 2 && !Number(item.text) || item.text.length >= 2 && Number(item.text) > 10) {
                    cssClass = 'is-column';
                }
            });

            return cssClass;
        },

        startTimer() {

            let startTime = Math.floor(Date.now() / 1000);
            let endTime;
            if (this.message.data) {
                endTime = this.message.data.endTime;
            }
            const interval = endTime - startTime;

            const run = () => {
                const now  = Math.floor(Date.now() / 1000);
                this.secondsLeft = endTime - now;
                this.loaderProgress = this.secondsLeft / interval * 100 ;
                if (this.loaderProgress <= 0 || endTime < Math.floor(Date.now() / 1000)) {
                    this.loaderProgress = 0;
                    clearInterval(this.timer);
                    this.setMessagesListPadding(0);
                }
            }

            this.timer = setInterval(run, 1000);
        },

        /**
         * Получить проголосовавших игроков
         * @param group
         * @param text
         * @returns {[]}
         */
        getVotedPlayers(group, text)
        {
            let players = [];

            if (!this.message.data.optionsGroups.length) {
                return players;
            }

            if (typeof this.message.data.optionsGroups[group] === "undefined") {
                return players;
            }

            this.message.data.optionsGroups[group].options.forEach((option) => {
                if (option.text === text) {
                    if (!this.message.data.anonymous) {
                        option.votersIds.forEach((id) => {
                            const votedPlayers = this.players.filter((player) => {
                                return player.id === id;
                            });
                            if (typeof votedPlayers[0] !== 'undefined') {
                                players.push(votedPlayers[0]);
                            }
                        })
                    }
                    if (this.message.data.anonymous && option.votesCount) {
                        for (let i = 0; i < option.votesCount; i++) {
                            players.push(1);
                        }
                    }
                }
            });

            return players;
        },

        /**
         * Проголосовать за один из вариантов
         */
        countVote(group, option)
        {
            EventApi.triggerEvent(EventApi.START_SOUND, {sound: 'buttonClick'});
            this.$parent.sendMessage('countVote', {
                'option': option,
                'group': group,
            });
            if (this.message.data.voters.indexOf(this.playerId) === -1) {
            } else {
                this.isVoted = true;
                this.setMessagesListPadding(0);
            }
        },
        /**
         * Установить отступ у списка сообщений
         * @param height
         */
        setMessagesListPadding(height)
        {
            this.$emit('voting-status-changed', height);
        },
        /**
         * Цвет игрока
         * @param playerID
         */
        getPlayerColor(playerID) {
            return this.$parent.getPlayerColor(playerID);
        },
        /**
         * Победитель голосования
         * @param group
         * @param option
         */
        isWinner(group, option) {
            const maxVotesValue = Math.max.apply(Math, this.message.data.optionsGroups[group].options.map((option) => {
                return option.votesCount; })
            );

            if (maxVotesValue === 0) {
                return false;
            }

            if (parseInt(option.votesCount) === parseInt(maxVotesValue)) {
                return true;
            }
            return false;
        },
        /**
         * Старт голосования
         */
        isVotingStarted(message)
        {
            const date = Math.floor(Date.now() / 1000);
            return message.command !== '/voteres'
                && !message.data.ended
                && (date < message.data.endTime || message.data.endTime === null);
        },
        /**
         * Конец голосования
         */
        isVotingEnded(message)
        {
            const date = Math.floor(Date.now() / 1000);
            if (message.data && message.data.ended && message.command === '/voteres') {
                return message.data.ended;
            }

            return message.command === '/voteres'
                && (date >= message.data.endTime || message.data.endTime === null);
        },

        /**
         * Опция недоступна
         * @param option
         * @return {boolean}
         */
        isDisabledOption(option)
        {
            /*if (this.message.data.voters.indexOf(this.playerId) !== -1) {
                return true;
            }*/

            return option.disabledFor.indexOf(this.playerId) !== -1;
        },

        /**
         * Опция выбрана
         * @param option
         * @return {boolean}
         */
        isSelectedOption(option)
        {
            return option.votersIds.indexOf(this.playerId) !== -1;
        },

        /**
         * Опция видима игроку
         * @param option
         * @returns {boolean}
         */
        isVisibleOption(option)
        {
            return option.hiddenFor.indexOf(this.playerId) === -1;
        }
    }

}
</script>

<style scoped>
.slide-fade-enter-active {
    transition: all 1s ease;
}
.slide-fade-leave-active {
    transition: all 1s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}
.slide-fade-enter, .slide-fade-leave-to
    /* .slide-fade-leave-active до версии 2.1.8 */ {
    transform: translateX(120%);
    opacity: 0;
}
</style>
